import { CARD_MODAL_TOP_INSET } from "@9amhealth/shared";
import styled from "@emotion/styled";
import { BottomSheet } from "src/ui/components/BottomSheet/BottomSheet";

export const CoachingSheet = styled(BottomSheet)`
  --sunrise-angle: 67.49deg;
  --dialog-background:
    linear-gradient(
      180deg,
      var(--color-cream, #fffcf3) 10.42%,
      rgba(255, 252, 243, 0) 100%
    ),
    linear-gradient(
      var(--sunrise-angle),
      #80b6ff 10.82%,
      var(--color-sunrise-pink, #f7bde6) 35.55%,
      var(--color-sunrise-orange, #ffbd70) 77.21%
    ),
    var(--color-white, #fff);

  [data-sheet-content] {
    padding: 3.875rem 1.5rem calc(var(--safe-area-inset-bottom) + 6.625rem);
  }

  @media (max-width: 767px) {
    && {
      height: calc(100vh - ${CARD_MODAL_TOP_INSET});
      height: calc(100dvh - ${CARD_MODAL_TOP_INSET});
      max-height: calc(100dvh - ${CARD_MODAL_TOP_INSET});
    }
  }

  @media (min-width: 768px) {
    [data-desktop-fullscreen] & {
      --sunrise-angle: 31.3deg;
      --border-radius: 0.5rem;
    }

    [data-desktop-fullscreen] & [data-sheet-content] {
      padding-top: 3.5rem;
      padding-bottom: 11.25rem;
    }
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 1.25rem;
  right: 0.75rem;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(33, 33, 33, 0.12);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  color: var(--color-charcoal, #212121);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition:
    background 0.15s ease,
    transform 0.15s ease;

  &:hover {
    background: rgba(33, 33, 33, 0.18);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--Accessible-Blue, #3973da);
    outline-offset: 2px;
  }

  @media (min-width: 768px) {
    [data-desktop-fullscreen] & {
      top: 0.75rem;
      right: 1.5rem;
    }
  }
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

export const UpperSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
`;

export const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: center;
`;

export const Headline = styled.h2`
  margin: 0;
  font-family: var(--font-family-serif);
  font-size: 2rem;
  font-weight: 300;
  line-height: 1.1875;
  letter-spacing: -0.015em;
  color: var(--color-charcoal, #212121);

  @media (min-width: 768px) {
    [data-desktop-fullscreen] & {
      font-size: 2.5rem;
      line-height: 1.1;
      letter-spacing: -0.025em;
    }
  }
`;

export const Description = styled.p`
  margin: 0;
  font-family: var(--font-family);
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.375;
  letter-spacing: -0.02em;
  color: var(--color-charcoal, #212121);
`;

export const Tiles = styled.div`
  display: flex;
  gap: 0.75rem;
  width: 100%;
`;

export const Tile = styled.div`
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 6.5625rem;
  padding: 0.625rem 1rem;
  border-radius: 1rem;
  box-sizing: border-box;

  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
  }

  &::before {
    backdrop-filter: blur(40px);
    -webkit-backdrop-filter: blur(40px);
    background: rgba(0, 0, 0, 0.08);
    mix-blend-mode: hard-light;
  }

  &::after {
    background: rgba(255, 255, 255, 0.07);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.5);
    mix-blend-mode: screen;
  }
`;

export const TileValueRow = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: baseline;
  font-family: var(--font-family);
  font-weight: 500;
  letter-spacing: -0.02em;
  color: var(--color-charcoal-80, rgba(33, 33, 33, 0.8));
`;

export const TrendIcon = styled.span`
  display: block;
  flex: 0 0 auto;
  color: var(--color-cream, #fffcf3);

  svg {
    display: block;
  }
`;

export const TileValue = styled.span`
  font-size: 1.875rem;
  line-height: 1;
`;

export const TileUnit = styled.span`
  font-size: 0.75rem;
  line-height: 1;
`;

export const TileLabel = styled.p`
  position: relative;
  z-index: 1;
  margin: 0;
  font-family: var(--font-family);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.0833;
  text-align: center;
  text-wrap: balance;
  color: var(--color-charcoal-80, rgba(33, 33, 33, 0.8));
`;

export const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding-top: 1rem;

  button * {
    text-transform: none;
  }
`;

export const Footnote = styled.p`
  margin: 0;
  font-family: var(--font-family);
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.3333;
  text-align: center;
  color: var(--color-charcoal-80, rgba(33, 33, 33, 0.8));
`;
